import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'mb-4 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: ' bg-blue-700 text-primary-foreground hover:bg-blue-800',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 md:w-auto',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        expandIcon:
          'group relative bg-blue-700 text-primary-foreground hover:bg-blue-800',
        ringHover:
          'bg-blue-700 text-primary-foreground transition-all duration-300 hover:bg-blue-800 hover:ring-2 hover:ring-primary/90 hover:ring-offset-2',
        shine:
          'animate-shine bg-gradient-to-r from-primary via-primary/75 to-primary bg-[length:400%_100%] text-primary-foreground ',
        gooeyRight:
          'relative z-0 overflow-hidden bg-blue-700 from-sky-400 text-primary-foreground transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r before:transition-transform before:duration-1000  hover:before:translate-x-0 hover:before:translate-y-0 ',
        gooeyLeft:
          'relative z-0 overflow-hidden bg-blue-700 from-sky-400 text-primary-foreground transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l after:transition-transform after:duration-1000  hover:after:translate-x-0 hover:after:translate-y-0 ',
        linkHover1:
          'relative after:absolute after:bottom-2 after:h-px after:w-3/4 after:origin-bottom-left after:scale-x-100 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-bottom-right hover:after:scale-x-0',
        linkHover2:
          'relative after:absolute after:bottom-2 after:h-px after:w-2/3 after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-bottom-left hover:after:scale-x-100',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-xl px-3',
        md: 'h-[45px] w-auto rounded-xl p-6',
        lg: 'focus:shadow-outline mx-0 h-11 w-full rounded-xl bg-blue-700 px-4 py-6 font-bold text-white hover:bg-blue-700 focus:outline-none sm:w-2/3 md:w-1/2 lg:w-1/3',
        xl: ' h-12 w-full rounded-xl bg-ring px-24 sm:w-[360px]',
        icon: 'size-10',
        xxl: 'focus:shadow-outline mx-0  h-[3.25rem] w-full rounded-xl bg-blue-700 px-4 py-6  font-bold text-white hover:bg-blue-700 focus:outline-none sm:w-[21.8125rem] md:w-[21.8125rem] lg:w-[21.8125rem] xl:w-[21.8125rem]  ',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

interface IconProps {
  Icon: React.ElementType
  iconPlacement: 'left' | 'right'
}

interface IconRefProps {
  Icon?: never
  iconPlacement?: 'left' | 'right'
}

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.action (e.g., "admin.login.submit", "forms.fd02.reject")
   */
  transactionName: string
}

export type ButtonIconProps = IconProps | IconRefProps

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & ButtonIconProps
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      Icon,
      iconPlacement,
      transactionName,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-transaction-name={transactionName}
        {...props}
      >
        {Icon && iconPlacement === 'left' && (
          <div className="group-hover:translate-x-100 w-0 translate-x-0 pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:pr-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
        <Slottable>{props.children}</Slottable>
        {Icon && iconPlacement === 'right' && (
          <div className="w-0 translate-x-full pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
